import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { FiArrowLeft, FiFileText, FiUser, FiBriefcase, FiMapPin, FiGlobe, FiCheckCircle, FiEdit } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActivityReferenceByIdRequest, updateActivityReferenceRequest } from '../../store/actions/dataActions';
import './AdminReferenceDetail.css';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children || 'N/A'}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminReferenceDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { referenceId } = useParams();
    const { selectedReference, updateReference } = useSelector((state) => state.data);

    const [showEditModal, setShowEditModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        category: '',
        prcp_is_cumulated: false,
        is_visible_reference: true,
        activity_type: '',
    });
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        if (referenceId) {
            dispatch(fetchActivityReferenceByIdRequest(referenceId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [referenceId]);

    const referenceInfo = selectedReference?.data?.reference || selectedReference?.data || null;
    const partnerInfo = selectedReference?.data?.partner || {};

    useEffect(() => {
        if (referenceInfo) {
            setFormData({
                name: referenceInfo.name || '',
                category: referenceInfo.category || '',
                prcp_is_cumulated: referenceInfo.prcp_is_cumulated || false,
                is_visible_reference: referenceInfo.is_visible_reference !== undefined ? referenceInfo.is_visible_reference : true,
                activity_type: referenceInfo.activity_type || '',
            });
        }
    }, [referenceInfo]);

    useEffect(() => {
        if (updateReference.success) {
            setSuccessMessage('Référence modifiée avec succès !');
            dispatch(fetchActivityReferenceByIdRequest(referenceId));
            const timer = setTimeout(() => {
                setShowEditModal(false);
                setSuccessMessage(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateReference.success, referenceId]);

    const handleEdit = () => {
        setSuccessMessage(null);
        setShowEditModal(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (referenceId) {
            dispatch(updateActivityReferenceRequest(referenceId, formData));
        }
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setSuccessMessage(null);
        if (referenceInfo) {
            setFormData({
                name: referenceInfo.name || '',
                category: referenceInfo.category || '',
                prcp_is_cumulated: referenceInfo.prcp_is_cumulated || false,
                is_visible_reference: referenceInfo.is_visible_reference !== undefined ? referenceInfo.is_visible_reference : true,
                activity_type: referenceInfo.activity_type || '',
            });
        }
    };

    if (selectedReference.loading) {
        return (
            <div className="dashboard-page admin-reference-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la référence</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/reference')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de la référence...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedReference.error) {
        return (
            <div className="dashboard-page admin-reference-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la référence</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/reference')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{selectedReference.error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!referenceInfo) {
        return (
            <div className="dashboard-page admin-reference-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de la référence</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/admin/reference')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de référence disponible pour l'identifiant: {referenceId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-reference-detail-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiFileText className="me-2" />
                                Détail de la référence
                            </h2>
                            <p className="text-muted mb-0">Code référence: {referenceInfo.reference_code || 'N/A'}</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/admin/reference')}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>

                    {/* Informations principales de la référence */}
                    <Section title="Informations principales">
                        <Row>
                            <Col md={6}>
                                <Label>Code référence</Label>
                                <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{referenceInfo.reference_code}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{referenceInfo.name || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Catégorie</Label>
                                <Value>{referenceInfo.category || 'N/A'}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Déclenchement cumulé</Label>
                                <Value>
                                    <Badge bg={referenceInfo.prcp_is_cumulated ? 'warning' : 'secondary'}>
                                        {referenceInfo.prcp_is_cumulated ? 'Oui' : 'Non'}
                                    </Badge>
                                </Value>
                            </Col>
                            <Col md={6}>
                                <Label>Référence visible</Label>
                                <Value>
                                    <Badge bg={referenceInfo.is_visible_reference ? 'success' : 'secondary'}>
                                        {referenceInfo.is_visible_reference ? 'Oui' : 'Non'}
                                    </Badge>
                                </Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations partenaire */}
                    {partnerInfo && Object.keys(partnerInfo).length > 0 && (
                        <Section title="Informations partenaire">
                            <Row>
                                <Col md={6}>
                                    <Label>Nom</Label>
                                    <Value>{partnerInfo.first_name} {partnerInfo.last_name}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Entreprise</Label>
                                    <Value>{partnerInfo.company_name || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Email</Label>
                                    <Value>{partnerInfo.email || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Téléphone</Label>
                                    <Value>{partnerInfo.phone_number || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Code utilisateur</Label>
                                    <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{partnerInfo.user_code || 'N/A'}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Code pays</Label>
                                    <Value>
                                        <Badge bg="primary">{partnerInfo.country_code || 'N/A'}</Badge>
                                    </Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={12}>
                                    <Label>Adresse</Label>
                                    <Value>
                                        <span style={{ whiteSpace: 'pre-line' }}>{partnerInfo.user_address || 'N/A'}</span>
                                    </Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={4}>
                                    <Label>Ville</Label>
                                    <Value>{partnerInfo.city || 'N/A'}</Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Code postal</Label>
                                    <Value>{partnerInfo.zip_code || 'N/A'}</Value>
                                </Col>
                                <Col md={4}>
                                    <Label>Pays</Label>
                                    <Value>{partnerInfo.country || 'N/A'}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Compte activé</Label>
                                    <Value>
                                        <Badge bg={partnerInfo.activate_account ? 'success' : 'secondary'}>
                                            <FiCheckCircle className="me-1" />
                                            {partnerInfo.activate_account ? 'Oui' : 'Non'}
                                        </Badge>
                                    </Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Vérifié</Label>
                                    <Value>
                                        <Badge bg={partnerInfo.is_verified ? 'success' : 'secondary'}>
                                            <FiCheckCircle className="me-1" />
                                            {partnerInfo.is_verified ? 'Oui' : 'Non'}
                                        </Badge>
                                    </Value>
                                </Col>
                            </Row>
                        </Section>
                    )}

                    {/* Modal d'édition */}
                    <Modal show={showEditModal} onHide={handleCloseModal} size="lg">
                        <Modal.Header closeButton>
                            <Modal.Title>Modifier la référence</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {successMessage && (
                                <Alert variant="success" className="mb-3">
                                    {successMessage}
                                </Alert>
                            )}
                            {updateReference.error && (
                                <Alert variant="danger" className="mb-3">
                                    {updateReference.error}
                                </Alert>
                            )}
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Nom *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateReference.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Catégorie *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="category"
                                                value={formData.category}
                                                onChange={handleInputChange}
                                                required
                                                disabled={updateReference.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Type d'activité</Form.Label>
                                            <Form.Select
                                                name="activity_type"
                                                value={formData.activity_type}
                                                onChange={handleInputChange}
                                                disabled={updateReference.loading}
                                            >
                                                <option value="">Sélectionner...</option>
                                                <option value="trip">Trip</option>
                                                <option value="activity">Activity</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                name="prcp_is_cumulated"
                                                label="Déclenchement cumulé"
                                                checked={formData.prcp_is_cumulated}
                                                onChange={handleInputChange}
                                                disabled={updateReference.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="checkbox"
                                                name="is_visible_reference"
                                                label="Référence visible"
                                                checked={formData.is_visible_reference}
                                                onChange={handleInputChange}
                                                disabled={updateReference.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Form>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseModal}
                                disabled={updateReference.loading}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={updateReference.loading}
                            >
                                {updateReference.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Modification...
                                    </>
                                ) : (
                                    'Enregistrer'
                                )}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminReferenceDetail;

